import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherAdminAPI } from '../../services/api';

function TeacherSupervisorRoster() {
  const queryClient = useQueryClient();
  const rosterQuery = useQuery(['supervisorRoster'], async () => {
    const res = await teacherAdminAPI.getSupervisorRoster();
    return res.data?.data ?? null;
  });
  const [draftBySupervisor, setDraftBySupervisor] = useState({});

  const rosterData = rosterQuery.data;
  const supervisors = useMemo(() => rosterData?.supervisors ?? [], [rosterData]);
  const unassigned = useMemo(() => rosterData?.unassigned ?? [], [rosterData]);
  const supervisorsMissingProfile = useMemo(
    () => rosterData?.supervisorsMissingProfile ?? [],
    [rosterData]
  );

  const allTeacherOptions = useMemo(() => {
    const map = new Map();
    for (const s of supervisors) {
      for (const t of s.teachers || []) {
        map.set(String(t.teacherId), t);
      }
    }
    for (const t of unassigned) {
      map.set(String(t.teacherId), t);
    }
    return [...map.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [supervisors, unassigned]);

  const getDraftIds = (supervisor) => {
    const sid = String(supervisor.supervisorTeacherId);
    if (draftBySupervisor[sid]) return draftBySupervisor[sid];
    return (supervisor.teachers || []).map((t) => String(t.teacherId));
  };

  const saveMutation = useMutation(
    ({ supervisorTeacherId, teacherIds }) =>
      teacherAdminAPI.updateSupervisorTeam(supervisorTeacherId, { teacherIds }),
    {
      onSuccess: () => {
        toast.success('Team updated');
        queryClient.invalidateQueries(['supervisorRoster']);
        setDraftBySupervisor({});
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to save team'),
    }
  );

  if (rosterQuery.isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rosterQuery.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Could not load supervisor roster.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960 }}>
      <Typography variant="h5" gutterBottom>
        Supervisor roster
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assign each classroom teacher to one supervisor. Teachers can only appear on one team.
      </Typography>

      {unassigned.length > 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {unassigned.length} teacher{unassigned.length === 1 ? '' : 's'} not assigned to a supervisor yet.
        </Alert>
      ) : null}

      {supervisorsMissingProfile.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These supervisor accounts need a teacher profile before you can assign teams:{' '}
          {supervisorsMissingProfile.map((s) => s.name || s.email).join(', ')}. Re-save the user in User
          management or contact an admin.
        </Alert>
      ) : null}

      {supervisors.length === 0 ? (
        <Alert severity="warning">
          No active <strong>teacher_supervisor</strong> users with a teacher profile were found. In User
          management, set the supervisor role and ensure each supervisor has a linked teacher profile (re-save
          the user if needed).
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {supervisors.map((supervisor) => {
          const sid = String(supervisor.supervisorTeacherId);
          const draftIds = getDraftIds(supervisor);
          const savedIds = (supervisor.teachers || []).map((t) => String(t.teacherId));
          const dirty =
            draftIds.length !== savedIds.length ||
            draftIds.some((id) => !savedIds.includes(id));

          return (
            <Card key={sid} variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1">{supervisor.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {supervisor.email}
                    </Typography>
                  </Box>
                  <Chip size="small" label={`${draftIds.length} teachers`} />
                </Stack>

                <FormControl fullWidth size="small">
                  <InputLabel>Team teachers</InputLabel>
                  <Select
                    multiple
                    value={draftIds}
                    onChange={(e) =>
                      setDraftBySupervisor((prev) => ({
                        ...prev,
                        [sid]: e.target.value,
                      }))
                    }
                    input={<OutlinedInput label="Team teachers" />}
                    renderValue={(selected) =>
                      selected
                        .map((id) => allTeacherOptions.find((t) => String(t.teacherId) === id)?.name || id)
                        .join(', ')
                    }
                  >
                    {allTeacherOptions.map((t) => (
                      <MenuItem key={t.teacherId} value={String(t.teacherId)}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!dirty || saveMutation.isLoading}
                    onClick={() =>
                      saveMutation.mutate({
                        supervisorTeacherId: sid,
                        teacherIds: draftIds,
                      })
                    }
                  >
                    {saveMutation.isLoading ? 'Saving…' : 'Save team'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

export default TeacherSupervisorRoster;
